import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  useNavigate,
  useParams,
} from "react-router-dom";
import { GraphQLClient } from "graphql-request";

import { graphqlEndpoint } from "../config";

import { useSnackbar } from "../components/snackbarProvider.jsx";

import { READ_OPERATIONAL_AUDIT } from "../graph/operationalAudits/queries";
import { SUBMIT_OPERATIONAL_AUDIT_RESPONSES } from "../graph/operationalAudits/mutations";
import { READ_AUDIT_QUESTIONS } from "../graph/auditQuestions/queries";

import {
  buildOperationalAuditSubmitPayload,
  calculateAuditProgress,
  createEmptyResponse,
  groupQuestionsByArea,
  normaliseResponseForDocumentationStatus,
  normaliseResponseForProcessStatus,
} from "../utils/operationalAuditResponses";

const client = new GraphQLClient(graphqlEndpoint);

const useRunOperationalAudit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setSnackbar } = useSnackbar();

  const [audit, setAudit] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState({});

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] =
    useState(false);

  useEffect(() => {
    let active = true;

    const fetchAuditAndQuestions = async () => {
      try {
        setLoading(true);

        const { readOperationalAudit } =
          await client.request(
            READ_OPERATIONAL_AUDIT,
            { id },
          );

        if (!active) {
          return;
        }

        setAudit(readOperationalAudit);

        const { readAuditQuestions } =
          await client.request(
            READ_AUDIT_QUESTIONS,
            {
              industry:
                readOperationalAudit.industry,
            },
          );

        if (!active) {
          return;
        }

        const industryQuestions =
          Array.isArray(readAuditQuestions)
            ? readAuditQuestions
            : [];

        const auditQuestionIDs =
          Array.isArray(
            readOperationalAudit.questionIDs,
          )
            ? readOperationalAudit.questionIDs
            : [];

        const scopedQuestions =
          auditQuestionIDs.length > 0
            ? industryQuestions.filter(
                (question) =>
                  auditQuestionIDs.includes(
                    question.id,
                  ),
              )
            : industryQuestions;

        setQuestions(scopedQuestions);
      } catch (error) {
        console.error(
          "Failed to load audit:",
          error,
        );

        if (!active) {
          return;
        }

        setSnackbar({
          open: true,
          severity: "error",
          message:
            error?.response?.errors?.[0]
              ?.message ||
            "Failed to load audit",
        });
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchAuditAndQuestions();

    return () => {
      active = false;
    };
  }, [id, setSnackbar]);

  const groupedQuestions = useMemo(
    () => groupQuestionsByArea(questions),
    [questions],
  );

  const progress = useMemo(
    () =>
      calculateAuditProgress({
        questions,
        responses,
      }),
    [questions, responses],
  );

  const auditLocked =
    audit?.status === "COMPLETED";

  const updateResponse = useCallback(
    (questionID, patch) => {
      setResponses((previousResponses) => ({
        ...previousResponses,

        [questionID]: {
          ...createEmptyResponse(),
          ...(previousResponses[questionID] ||
            {}),
          answered: true,
          ...patch,
        },
      }));
    },
    [],
  );

  const handleProcessStatusChange =
    useCallback((questionID, nextStatus) => {
      setResponses((previousResponses) => {
        const currentResponse =
          previousResponses[questionID] ||
          createEmptyResponse();

        return {
          ...previousResponses,

          [questionID]:
            normaliseResponseForProcessStatus({
              currentResponse,
              nextStatus,
            }),
        };
      });
    }, []);

  const handleDocumentationStatusChange =
    useCallback(
      (
        questionID,
        documentationStatus,
      ) => {
        setResponses((previousResponses) => {
          const currentResponse =
            previousResponses[questionID] ||
            createEmptyResponse();

          return {
            ...previousResponses,

            [questionID]:
              normaliseResponseForDocumentationStatus(
                {
                  currentResponse,
                  documentationStatus,
                },
              ),
          };
        });
      },
      [],
    );

  const handleSubmit = useCallback(async () => {
    if (progress.remaining > 0) {
      setSnackbar({
        open: true,
        severity: "warning",
        message: `${progress.remaining} ${
          progress.remaining === 1
            ? "activity still needs"
            : "activities still need"
        } to be assessed`,
      });

      return;
    }

    const payload =
      buildOperationalAuditSubmitPayload({
        questions,
        responses,
      });

    if (payload.length === 0) {
      setSnackbar({
        open: true,
        severity: "warning",
        message:
          "Assess at least one audit activity before submitting",
      });

      return;
    }

    try {
      setSubmitting(true);

      await client.request(
        SUBMIT_OPERATIONAL_AUDIT_RESPONSES,
        {
          input: {
            auditId: id,
            responses: payload,
          },
        },
      );

      setSnackbar({
        open: true,
        severity: "success",
        message:
          "Operational audit completed successfully",
      });

      navigate("/manageOperationalAudits");
    } catch (error) {
      console.error(
        "Failed to submit operational audit:",
        error,
      );

      setSnackbar({
        open: true,
        severity: "error",
        message:
          error?.response?.errors?.[0]
            ?.message ||
          "Failed to submit operational audit",
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    id,
    navigate,
    progress.remaining,
    questions,
    responses,
    setSnackbar,
  ]);

  const handleCancel = useCallback(() => {
    navigate("/manageOperationalAudits");
  }, [navigate]);

  return {
    audit,
    questions,
    groupedQuestions,
    responses,
    progress,

    loading,
    submitting,
    auditLocked,

    updateResponse,
    handleProcessStatusChange,
    handleDocumentationStatusChange,

    handleSubmit,
    handleCancel,
  };
};

export default useRunOperationalAudit;